/**
 * 邀请页面配置管理页面
 * 管理员可以编辑邀请页面的海报图片
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { LayoutWithSidebar } from '@/components/LayoutWithSidebar';
import { useAuthStore } from '@/stores/auth';
import { apiClient } from '@/services/api';
import type { PosterImage } from '@xiaozhi/shared-types';
import { toast } from 'sonner';
import {
  Loader2,
  Plus,
  Trash2,
  Save,
  Image as ImageIcon,
  ExternalLink,
  GripVertical,
  Upload,
  Eye,
  EyeOff,
} from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';

// Sortable Item Component
interface SortableItemProps {
  poster: any;
  index: number;
  onEdit: (poster: any, index: number) => void;
  onDelete: (index: number) => void;
  onToggle: (index: number) => void;
}

function SortableItem({ poster, index, onEdit, onDelete, onToggle }: SortableItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: poster.url,
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border hover:bg-muted/50 transition-colors"
    >
      <button
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-5 w-5" />
      </button>

      <div className="h-16 w-16 rounded bg-background overflow-hidden flex-shrink-0">
        <img
          src={poster.url}
          alt={poster.title || '海报'}
          className="h-full w-full object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/placeholder-image.png';
          }}
        />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium truncate">{poster.title || '未命名'}</span>
          <Badge variant={poster.enabled ? 'default' : 'secondary'} className="text-xs">
            {poster.enabled ? '已启用' : '已禁用'}
          </Badge>
        </div>
        {poster.description && (
          <p className="text-sm text-muted-foreground truncate">{poster.description}</p>
        )}
        {poster.link && (
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <ExternalLink className="h-3 w-3" />
            {poster.link}
          </p>
        )}
      </div>

      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onToggle(index)}
          title={poster.enabled ? '禁用' : '启用'}
        >
          {poster.enabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onEdit(poster, index)}
          title="编辑"
        >
          <Upload className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(index)}
          title="删除"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export function InvitationPageManagementPage() {
  const { accessToken, user } = useAuthStore();
  const navigate = useNavigate();

  const [config, setConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    enabled: false,
    layout: 'carousel' as 'carousel' | 'grid' | 'single',
    autoPlay: true,
    interval: 3000,
  });

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'super_admin') {
      toast.error('需要管理员权限');
      navigate('/admin');
      return;
    }
    if (accessToken) {
      loadConfig();
    }
  }, [accessToken]);

  const loadConfig = async () => {
    try {
      const data = await apiClient.getConfig();
      setConfig(data);
      if (data?.invitationPage) {
        setFormData({
          enabled: data.invitationPage.enabled ?? true,
          layout: data.invitationPage.layout ?? 'carousel',
          autoPlay: data.invitationPage.autoPlay ?? true,
          interval: data.invitationPage.interval ?? 3000,
        });
      }
    } catch (error) {
      console.error('加载配置失败:', error);
      toast.error('加载配置失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config) return;

    try {
      setSaving(true);
      const updatedConfig = {
        ...config,
        invitationPage: {
          ...formData,
          posterImages: config.invitationPage?.posterImages || [],
        },
      };
      await apiClient.updateConfig(updatedConfig);
      toast.success('保存成功');
      await loadConfig();
    } catch (error) {
      console.error('保存失败:', error);
      toast.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async () => {
    if (!accessToken || uploadedFiles.length === 0) return;

    try {
      setUploading(true);
      const newPosters: PosterImage[] = [];

      for (let i = 0; i < uploadedFiles.length; i++) {
        const file: any = uploadedFiles[i];
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch('/api/upload/image', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        });

        const result = await response.json();
        if (result.success && result.data) {
          newPosters.push({
            url: result.data.url,
            title: file.name.split('.')[0],
            sortOrder: (config?.invitationPage?.posterImages?.length || 0) + i,
            enabled: true,
          });
        }
      }

      if (newPosters.length > 0) {
        setConfig((prev: any) => ({
          ...prev,
          invitationPage: {
            ...prev.invitationPage,
            posterImages: [...(prev.invitationPage?.posterImages || []), ...newPosters],
          },
        }));
        toast.success(`成功上传 ${newPosters.length} 张图片`);
      }

      setUploadDialog(false);
      setUploadedFiles([]);
      setPreviewUrls([]);
    } catch (error) {
      console.error('上传失败:', error);
      toast.error('上传失败');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePoster = async (index: number) => {
    if (!config?.invitationPage?.posterImages) return;

    const newPosters = [...config.invitationPage.posterImages];
    newPosters.splice(index, 1);

    setConfig((prev: any) => ({
      ...prev,
      invitationPage: {
        ...prev.invitationPage,
        posterImages: newPosters,
      },
    }));
    toast.success('已删除');
  };

  const handleTogglePoster = async (index: number) => {
    if (!config?.invitationPage?.posterImages) return;

    const newPosters = [...config.invitationPage.posterImages];
    newPosters[index] = {
      ...newPosters[index],
      enabled: !newPosters[index].enabled,
    };

    setConfig((prev: any) => ({
      ...prev,
      invitationPage: {
        ...prev.invitationPage,
        posterImages: newPosters,
      },
    }));
  };

  const handleEditPoster = () => {
    setUploadDialog(true);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!config?.invitationPage?.posterImages || !over || active.id === over.id) return;

    const oldIndex = config.invitationPage.posterImages.findIndex((p: any) => p.url === active.id);
    const newIndex = config.invitationPage.posterImages.findIndex((p: any) => p.url === over.id);

    const newPosters = arrayMove(config.invitationPage.posterImages, oldIndex, newIndex);
    setConfig((prev: any) => ({
      ...prev,
      invitationPage: {
        ...prev.invitationPage,
        posterImages: newPosters,
      },
    }));
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const newFiles = Array.from(files);
    const validFiles = newFiles.filter((file) => {
      const isValid = file.type.startsWith('image/');
      if (!isValid) {
        toast.error(`${file.name} 不是图片文件`);
      }
      return isValid;
    });

    if (validFiles.length === 0) return;

    setUploadedFiles(validFiles);

    // Create preview URLs
    const urls = validFiles.map((file) => URL.createObjectURL(file));
    setPreviewUrls(urls);
  };

  if (loading) {
    return (
      <LayoutWithSidebar>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </LayoutWithSidebar>
    );
  }

  const posters = config?.invitationPage?.posterImages || [];

  return (
    <LayoutWithSidebar>
      <div className="flex-1 space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">邀请页面配置</h1>
            <p className="text-muted-foreground mt-1">
              管理邀请页面的海报图片和展示设置
            </p>
          </div>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                保存中...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                保存配置
              </>
            )}
          </Button>
        </div>

        {/* 基础设置 */}
        <Card>
          <CardHeader>
            <CardTitle>基础设置</CardTitle>
            <CardDescription>配置海报的展示方式</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>启用海报展示</Label>
                <p className="text-sm text-muted-foreground">在邀请页面显示海报图片</p>
              </div>
              <Switch
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
              />
            </div>

            <div className="space-y-2">
              <Label>布局模式</Label>
              <div className="flex gap-2">
                {(['carousel', 'grid', 'single'] as const).map((layout) => (
                  <Button
                    key={layout}
                    variant={formData.layout === layout ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFormData({ ...formData, layout })}
                  >
                    {layout === 'carousel' ? '轮播' : layout === 'grid' ? '网格' : '单图'}
                  </Button>
                ))}
              </div>
            </div>

            {formData.layout === 'carousel' && (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>自动播放</Label>
                    <p className="text-sm text-muted-foreground">轮播图自动切换</p>
                  </div>
                  <Switch
                    checked={formData.autoPlay}
                    onCheckedChange={(checked) => setFormData({ ...formData, autoPlay: checked })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>切换间隔 (毫秒)</Label>
                  <Input
                    type="number"
                    value={formData.interval}
                    onChange={(e) => setFormData({ ...formData, interval: parseInt(e.target.value) || 3000 })}
                    min="1000"
                    max="10000"
                    step="500"
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* 海报列表 */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>海报列表</CardTitle>
                <CardDescription>
                  拖拽可排序，共 {posters.length} 张海报
                </CardDescription>
              </div>
              <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    添加海报
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>上传海报图片</DialogTitle>
                    <DialogDescription>
                      支持 jpg、png、gif、webp 格式，单张图片最大 5MB
                    </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-4 py-4">
                    <div
                      className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => document.getElementById('file-input')?.click()}
                    >
                      <input
                        id="file-input"
                        type="file"
                        multiple
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleFileSelect(e.target.files)}
                      />
                      <ImageIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        点击或拖拽图片到此处上传
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        支持多张图片同时上传
                      </p>
                    </div>

                    {previewUrls.length > 0 && (
                      <div className="grid grid-cols-3 gap-2">
                        {previewUrls.map((url, index) => (
                          <div key={index} className="relative aspect-video bg-muted rounded overflow-hidden">
                            <img src={url} alt={`预览 ${index + 1}`} className="h-full w-full object-cover" />
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setUploadDialog(false)}>
                        取消
                      </Button>
                      <Button onClick={handleImageUpload} disabled={uploading || uploadedFiles.length === 0}>
                        {uploading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            上传中...
                          </>
                        ) : (
                          <>
                            <Upload className="mr-2 h-4 w-4" />
                            确认上传
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {posters.length === 0 ? (
              <div className="text-center py-12">
                <ImageIcon className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground mb-4">暂无海报图片</p>
                <Button onClick={() => setUploadDialog(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  添加第一张海报
                </Button>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={posters.map((p: any) => p.url)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {posters.map((poster: any, index: number) => (
                      <SortableItem
                        key={poster.url}
                        poster={poster}
                        index={index}
                        onEdit={handleEditPoster}
                        onDelete={handleDeletePoster}
                        onToggle={handleTogglePoster}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </CardContent>
        </Card>
      </div>
    </LayoutWithSidebar>
  );
}
